import React, { useState, useEffect } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
// template components
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import InPageLeadForm from '@nmx/template/dist/componentLibrary/modules/InPageLeadForm';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import FinancialPlanningSection from './components/financialPlanningSection';
import HeroSection from './components/heroSection';
import InPageLeadFormLazyLoadContainer from '../../components/inPageLeadFormLazyLoadContainer';
import IntroSection from './components/introSection';
import Metadata from './components/metadata';
import ProductCalloutGrouping1 from './components/productCalloutGrouping1';
import ProductCalloutGrouping2 from './components/productCalloutGrouping2';
import RelatedLinksSection from './components/relatedLinksSection';
import SectionVideoPlayerSection from './components/sectionVideoPlayerSection';
// images for prefetch
import highResDesktopImage from './components/heroSection/images/ltc-planning-hero.webp';
import highResMobileImage from './components/heroSection/images/ltc-planning-hero-mobile.webp';
// dislaimer footnotes
import { LoyaltyBasedOnClientData } from '../../../utils/disclaimerFootnotes';
// other utils
import { getImmediateMatchFeatureFlag } from '../../../utils/getFeatureFlags/getFeatureFlags';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const [immediateMatchFeatureFlag, setImmediateMatchFeatureFlag] = useState(false);

  useEffect(() => {
    getImmediateMatchFeatureFlag(setImmediateMatchFeatureFlag, config);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      dynamicConfig={dynamicConfig}
      config={config}
      useReCaptcha={loadRecaptcha}
      disableDisclaimerTopBorder={false}
      disclaimerFootnotes={[
        <>
          <sup>1</sup>&nbsp;Long&#45;Term Services and Supports for Older Americans: Risks and Financing Research Brief. Office of the Assistant Secretary for Planning and Evaluation, February 2016.
        </>,
        <>
          <sup>2</sup>&nbsp;Northwestern Mutual C.A.R.E (Costs, Accountabilities, Realities, Expectations) Study, 2018
        </>,
        <>
          <sup>3</sup>&nbsp;Utilizing the accumulated value through policy loans, surrenders, or cash withdrawals will reduce the death benefit; and may necessitate greater outlay than anticipated and&#47;or result in an unexpected taxable event.
        </>,
        <>
          {LoyaltyBasedOnClientData(4)}
        </>,
      ]}
      customDisclaimers={[
        <>
          This policy includes exclusions and limitations. For costs and further details of the coverage, including exclusions, any reductions or limitations and terms under which the policy may be continued in force, see your licensed insurance agent&#47;producer or write to the company. The purpose of this material is for the marketing and solicitation of insurance. A financial representative (a licensed insurance agent&#47;producer) may contact you.
        </>,
        <>
          Northwestern Long Term Care Insurance Company, a subsidiary of The Northwestern Mutual Life Insurance Company, Milwaukee, WI.
        </>,
        <>
          Northwestern Mutual is the marketing name for The Northwestern Mutual Life Insurance Company, Milwaukee, WI (NM) (life insurance, disability insurance, annuities, and life insurance with long&#45;term care benefits).
        </>,
        <>
          Long&#45;term care insurance is issued by Northwestern Long Term Care Insurance Company, Milwaukee, WI, a subsidiary of NM.
        </>,
      ]}
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Insurance',
          linkUrl: '/insurance/',
          linkAriaLabel: 'Northwestern Mutual Insurance',
        },
        {
          pageName: 'Long Term Care Planning',
          linkUrl: '/long-term-care-planning/',
          linkAriaLabel: 'Northwestern Mutual Long Term Care Planning',
        },
      ]}>
      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={highResMobileImage} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={highResDesktopImage} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      <Metadata
        image={highResDesktopImage}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
      />

      <HeroSection />

      <IntroSection />

      <ProductCalloutGrouping1 />

      <SectionVideoPlayerSection/>

      <FinancialPlanningSection />

      <ProductCalloutGrouping2 />

      <InPageLeadFormLazyLoadContainer>
        <InPageLeadForm
          sectionId='in-page-lead' // note: id in-page-lead-form will be on the Form element itself.
          config={config}
          formLocation='long-term-care-planning'
          immediateMatchFeatureFlagEnabled={immediateMatchFeatureFlag}
          onFormEngaged={() => {
            if (!loadRecaptcha) {
              setLoadRecaptcha(true);
            }
          }}
          variant='embedded'
        />
      </InPageLeadFormLazyLoadContainer>

      <CredentialCalloutGrouping />

      <ContentHub
        sectionId='content-hub'
        backgroundVariant='lightC'
        headingText={<>Boost your long&#45;term care knowledge</>}
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

      <AdvisorConnectModule />

      <RelatedLinksSection />

    </Page>
  );
};
